/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * DropdownShell — 220px sidebar with a clickable header that opens a module dropdown.
 * Closest to the current UX. Active module logo + name in header with chevron.
 */

import { AppVersion } from '@/components/ui/AppVersion';
import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { ChevronDown, Check } from 'lucide-react';
import { useTheme } from '@/lib/theme';
import { projectConfig } from '@/config/project.config';
import type { ModuleShellProps } from './types';

export function DropdownShell({ modules, activeModule, onModuleChange, children }: ModuleShellProps) {
  const { t } = useTranslation('common');
  const { isDark } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const logo = isDark ? activeModule.logos.dark : activeModule.logos.light;

  // Close dropdown on click outside
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  return (
    <aside className="ms-dropdown-sidebar">
      {/* Header — clickable to open dropdown */}
      <div className="ms-dropdown-header" ref={dropdownRef}>
        <button
          className="ms-dropdown-trigger"
          onClick={() => setIsOpen(!isOpen)}
          type="button"
        >
          <div className="ms-dropdown-brand">
            <div className="ms-dropdown-logo">
              <img src={logo} alt={t(activeModule.nameKey)} />
            </div>
            <div className="ms-dropdown-brand-text">
              <div className="ms-dropdown-title">{t(activeModule.nameKey)}</div>
              <div className="ms-dropdown-subtitle">{t(activeModule.subtitleKey)}</div>
            </div>
          </div>
          <ChevronDown
            size={14}
            className={`ms-dropdown-chevron ${isOpen ? 'open' : ''}`}
          />
        </button>

        {/* Dropdown panel */}
        {isOpen && (
          <div className="ms-dropdown-panel">
            <div className="ms-dropdown-panel-label">
              {t('moduleSwitcher.switchModule')}
            </div>
            {modules.map((mod) => {
              const modLogo = isDark ? mod.logos.dark : mod.logos.light;
              const isActive = mod.id === activeModule.id;
              return (
                <button
                  key={mod.id}
                  className={`ms-dropdown-option ${isActive ? 'active' : ''}`}
                  onClick={() => {
                    if (!isActive) onModuleChange(mod);
                    setIsOpen(false);
                  }}
                  type="button"
                >
                  <div className="ms-dropdown-option-logo">
                    <img src={modLogo} alt={t(mod.nameKey)} />
                  </div>
                  <div className="ms-dropdown-option-text">
                    <span className="ms-dropdown-option-name">{t(mod.nameKey)}</span>
                    <span className="ms-dropdown-option-sub">{t(mod.subtitleKey)}</span>
                  </div>
                  {isActive && <Check size={14} className="ms-dropdown-check" />}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Nav content */}
      <div className="ms-dropdown-nav">{children}</div>

      {/* Footer — legal/copyright only */}
      <div className="ms-dropdown-footer">
        <div className="ms-dropdown-legal">
          <span>&copy; 2026 CoreX-Suite</span>
          <span className="ms-dropdown-legal-sep">&middot;</span>
          <NavLink to="/privacy" className="ms-dropdown-legal-link">{t('core:sidebar.privacy', 'Privacy')}</NavLink>
          <span className="ms-dropdown-legal-sep">&middot;</span>
          <NavLink to="/terms" className="ms-dropdown-legal-link">{t('core:sidebar.terms', 'Terms')}</NavLink>
          <span className="ms-dropdown-legal-sep">&middot;</span>
          <AppVersion />
        </div>
      </div>

      <style>{styles}</style>
    </aside>
  );
}

const styles = `
  .ms-dropdown-sidebar {
    --module-shell-width: 220px;
    position: fixed;
    top: 0;
    left: 0;
    width: 220px;
    height: 100vh;
    background: var(--color-bg-secondary);
    border-right: 1px solid var(--color-border-subtle);
    display: flex;
    flex-direction: column;
    z-index: 100;
  }

  .ms-dropdown-header {
    position: relative;
    flex-shrink: 0;
  }

  .ms-dropdown-trigger {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    border: none;
    border-bottom: 1px solid var(--color-border-subtle);
    background: transparent;
    cursor: pointer;
    transition: background 0.15s;
  }

  .ms-dropdown-trigger:hover {
    background: var(--color-bg-hover);
  }

  .ms-dropdown-brand {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .ms-dropdown-logo {
    width: 32px;
    height: 32px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .ms-dropdown-logo img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .ms-dropdown-brand-text {
    text-align: left;
  }

  .ms-dropdown-title {
    font-family: var(--font-heading);
    font-size: 13px;
    font-weight: 600;
    color: var(--color-text-primary);
    line-height: 1.2;
  }

  .ms-dropdown-subtitle {
    font-size: 10px;
    color: var(--color-text-secondary);
  }

  .ms-dropdown-chevron {
    color: var(--color-text-muted);
    transition: transform 0.2s ease;
    flex-shrink: 0;
  }

  .ms-dropdown-chevron.open {
    transform: rotate(180deg);
  }

  /* Dropdown panel */
  .ms-dropdown-panel {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-default);
    border-top: none;
    border-radius: 0 0 8px 8px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
    z-index: 200;
    padding: 4px;
    animation: ms-dropdown-slide 0.15s ease;
  }

  @keyframes ms-dropdown-slide {
    from { opacity: 0; transform: translateY(-4px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .ms-dropdown-panel-label {
    padding: 6px 10px 4px;
    font-size: 9px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--color-text-muted);
  }

  .ms-dropdown-option {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    border: none;
    border-radius: 6px;
    background: transparent;
    cursor: pointer;
    text-align: left;
    transition: background 0.1s;
  }

  .ms-dropdown-option:hover {
    background: var(--color-bg-hover);
  }

  .ms-dropdown-option.active {
    background: rgba(139, 0, 0, 0.08);
  }

  .ms-dropdown-option-logo {
    width: 24px;
    height: 24px;
    border-radius: 4px;
    flex-shrink: 0;
  }

  .ms-dropdown-option-logo img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .ms-dropdown-option-text {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }

  .ms-dropdown-option-name {
    font-size: 12px;
    font-weight: 500;
    color: var(--color-text-primary);
  }

  .ms-dropdown-option-sub {
    font-size: 10px;
    color: var(--color-text-muted);
  }

  .ms-dropdown-check {
    color: var(--color-brand);
    flex-shrink: 0;
  }

  .ms-dropdown-nav {
    flex: 1;
    overflow-y: auto;
    padding: 4px 8px;
  }

  .ms-dropdown-footer {
    border-top: 1px solid var(--color-border-subtle);
    padding: 0;
    flex-shrink: 0;
  }

  .ms-dropdown-legal {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    padding: 6px 12px;
    font-size: 0.6rem;
    color: var(--color-text-muted);
  }

  .ms-dropdown-legal-sep {
    margin: 0 4px;
    color: var(--color-border-subtle);
  }

  .ms-dropdown-legal-link {
    color: var(--color-text-muted);
    text-decoration: none;
    font-size: 0.6rem;
  }

  .ms-dropdown-legal-link:hover {
    color: var(--color-text-secondary);
  }

  @media (max-width: 768px) {
    .ms-dropdown-sidebar {
      transform: translateX(-100%);
      transition: transform 0.3s ease;
    }
  }
`;
